// msal-instances.ts
import { InjectionToken } from '@angular/core';
import { Configuration, BrowserCacheLocation, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { RuntimeConfigService } from './_shared/services/config/runtime-config.service';
import { buildB2CAuthorityUrl, buildEntraAuthorityUrl } from './auth.config';

/**
 * Injection tokens for different MSAL instances
 */
export const MSAL_ENTRA_INSTANCE = new InjectionToken<PublicClientApplication>('MSAL_ENTRA_INSTANCE');
export const MSAL_B2C_INSTANCE = new InjectionToken<PublicClientApplication>('MSAL_B2C_INSTANCE');

/**
 * Factory function to create an MSAL instance for Entra ID authentication
 */
export function MSALEntraInstanceFactory(runtimeConfigService: RuntimeConfigService): PublicClientApplication {
  const runtimeConfig = runtimeConfigService.config;
  
  const msalConfig: Configuration = {
    auth: {
      clientId: runtimeConfig.shiftSSOClientId,
      authority: runtimeConfig.shiftSSOAuthority,
      redirectUri: `${window.location.origin}/login-entra-callback`,
      postLogoutRedirectUri: `${window.location.origin}`,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message, containsPii) => {
          console.log('[Entra MSAL]', message);
        },
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  };

  console.log('[Entra MSAL] Configuration:', {
    clientId: msalConfig.auth.clientId,
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri
  });

  return new PublicClientApplication(msalConfig);
}

/**
 * Factory function to create an MSAL instance for B2C authentication
 */
export function MSALB2CInstanceFactory(runtimeConfigService: RuntimeConfigService): PublicClientApplication {
  const runtimeConfig = runtimeConfigService.config;
  
  // Get the client ID directly from the AzureB2C section of the settings
  const azureB2CSettings = runtimeConfig.AzureB2C;
  const authB2CConfig = runtimeConfig.auth?.azureB2C;

  console.log('[B2C MSAL] Runtime config:', {
    hasAzureB2CSettings: !!azureB2CSettings,
    hasAuthB2CConfig: !!authB2CConfig,
    azureB2CClientId: azureB2CSettings?.ClientId || 'undefined',
    authB2CTenant: (authB2CConfig as any)?.tenant || 'undefined'
  });

  // First try to get values from the root AzureB2C section (backend provided)
  // Then fall back to the auth.azureB2C section (UI configured)
  const tenantName = azureB2CSettings?.TenantName || 
                     (authB2CConfig as any)?.tenant || 
                     'shiftdigitalinsightdev';
  
  const policy = azureB2CSettings?.SignInPolicyName || 
                 (authB2CConfig as any)?.policy || 
                 'B2C_1_SignIn';
  
  // Use the client ID from the Azure B2C configuration - this is critical to fix the AADB2C90018 error
  const clientId = azureB2CSettings?.ClientId || 
                  (authB2CConfig as any)?.clientId || 
                  'e752deba-c42f-4cd9-b868-589fe2cac415';
  
  // Configure the B2C authority URL with the standard policy
  const authorityUrl = buildB2CAuthorityUrl(tenantName, policy);
  
  console.log('[B2C MSAL] Using B2C configuration:', {
    tenantName,
    policy,
    clientId,
    authorityUrl
  });

  const msalConfig: Configuration = {
    auth: {
      clientId: clientId,
      authority: authorityUrl,
      knownAuthorities: [`${tenantName}.b2clogin.com`],
      redirectUri: `${window.location.origin}/login-b2c-callback`,
      postLogoutRedirectUri: `${window.location.origin}`,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message, containsPii) => {
          console.log('[B2C MSAL]', message);
        },
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  };

  console.log('[B2C MSAL] Configuration:', {
    clientId: msalConfig.auth.clientId,
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri,
    knownAuthorities: msalConfig.auth.knownAuthorities
  });

  return new PublicClientApplication(msalConfig);
}
